// umlaut-check: skip — "zuruecksetzen"/"naechste"/"gemerkt" u. ä. sind
// technische Bezeichner (Funktions-/Variablennamen), bleiben ASCII.

import {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {router, usePage} from "@inertiajs/react";

/**
 * Filter UND Sortierung je Seite im Local Storage merken (Rückmeldung T12:
 * "Wenn ich von der Seite Lieferfähigkeit auf eine Detailseite navigiere und
 * ... wieder zurück, dann sollen alle Filter ... erhalten bleiben ... Das
 * gilt generell für alle Bereiche"). Ursprünglich für den Bereich Cosmos
 * gebaut, an dieser Stelle allgemein für die ganze Anwendung nutzbar.
 *
 * Drei Varianten je nachdem, wie eine Seite ihren Filterzustand hält:
 *   - `useGemerkteFilter` (Standard-Export): Filter leben rein im Browser
 *     (eigener React-State, in der URL gespiegelt) - passend für Seiten wie
 *     Cosmos oder Users.jsx.
 *   - `useGemerkteServerFilter`: Filter/Suche/Sortierung leben serverseitig
 *     und kommen über Inertia-Page-Props zurück (InertiaTable-Seiten wie
 *     Weclapp-Listen, Bestellungen, Produkte). Merkt sich die Query-String
 *     und stellt sie beim Aufruf ohne Parameter per Hintergrund-Anfrage
 *     wieder her, ohne dass ein Browser-Reload nötig ist.
 *   - `useGemerkteDataGridState`: für die eigene DataGrid-Komponente
 *     (Components/DataGrid/DataGrid.jsx), die ihren Zustand über
 *     `initialSorting`/`initialColumnFilters`/... und `onStateChange` nach
 *     außen reicht (Vorbild: die gespeicherten Ansichten der
 *     Produktinformationen, hier ohne eigene Namensverwaltung).
 *
 * Vorrang bei der ersten Anzeige, überall gleich: ausdrücklicher
 * URL-Parameter (Link teilen, Browser-Zurück) > gemerkter Stand aus dem
 * letzten Besuch > mitgegebener Standardwert. Jeder Speicherzugriff steht in
 * try/catch - im privaten Fenster oder bei vollem Kontingent verhält sich
 * die Seite dann einfach wie ganz ohne Gedächtnis, statt zu reißen.
 */
function speicherSchluessel(vorsilbe, seite, benutzerId) {
    return `${vorsilbe}.${seite}${benutzerId !== undefined && benutzerId !== null ? "." + benutzerId : ""}`;
}

/** Roh-Lesezugriff auf den Local Storage - generisch für alle drei Varianten. */
export function gemerkteWerteLesen(schluessel) {
    try {
        const roh = window.localStorage.getItem(schluessel);
        const wert = roh ? JSON.parse(roh) : null;
        return wert && typeof wert === "object" && !Array.isArray(wert) ? wert : {};
    } catch {
        return {};
    }
}

/** Roh-Schreibzugriff auf den Local Storage - generisch für alle drei Varianten. */
export function gemerkteWerteSchreiben(schluessel, werte) {
    try {
        window.localStorage.setItem(schluessel, JSON.stringify(werte));
    } catch {
        // Local Storage nicht verfügbar (privates Fenster, Kontingent) - die
        // Filter gelten dann nur für diesen Tab, bis er geschlossen wird.
    }
}

function gemerktenEintragLoeschen(schluessel) {
    try {
        window.localStorage.removeItem(schluessel);
    } catch {
        // s. o.
    }
}

function gleich(a, b) {
    if (Array.isArray(a) || Array.isArray(b)) {
        const listeA = Array.isArray(a) ? a : [];
        const listeB = Array.isArray(b) ? b : [];
        return listeA.length === listeB.length && listeA.every((wert, i) => wert === listeB[i]);
    }
    return String(a ?? "") === String(b ?? "");
}

function codieren(wert) {
    if (Array.isArray(wert)) {
        return wert.join(",");
    }
    if (typeof wert === "boolean") {
        return wert ? "1" : "0";
    }
    return wert === null || wert === undefined ? "" : String(wert);
}

function decodieren(urlWert, standardWert) {
    if (Array.isArray(standardWert)) {
        return urlWert ? urlWert.split(",").filter(Boolean) : [];
    }
    if (typeof standardWert === "boolean") {
        return urlWert === "1";
    }
    return urlWert;
}

function anfangsWerte(schluessel, standard) {
    const params = new URLSearchParams(window.location.search);
    const gemerkt = gemerkteWerteLesen(schluessel);
    const werte = {};
    for (const feld of Object.keys(standard)) {
        if (params.has(feld)) {
            werte[feld] = decodieren(params.get(feld), standard[feld]);
        } else if (Object.prototype.hasOwnProperty.call(gemerkt, feld)) {
            werte[feld] = gemerkt[feld];
        } else {
            werte[feld] = standard[feld];
        }
    }
    return werte;
}

/**
 * @param {string} seite - eindeutiger Name der Seite (z. B. "lieferfaehigkeit").
 * @param {Object<string, *>} standard - Ausgangswerte aller gemerkten Felder.
 * @param {{benutzerId?: string|number}} [optionen]
 * @returns {{werte: Object, setWert: Function, setWerte: Function, zuruecksetzen: Function, weichtAb: boolean}}
 */
export default function useGemerkteFilter(seite, standard, {benutzerId} = {}) {
    const standardRef = useRef(standard);
    standardRef.current = standard;
    const schluessel = useMemo(() => speicherSchluessel("filter", seite, benutzerId), [seite, benutzerId]);

    const [werte, setWerteState] = useState(() => anfangsWerte(schluessel, standard));

    // URL-Sync ohne Seitenwechsel (history.replaceState, wie useQueryState) -
    // nur abweichende Felder erscheinen in der Adresszeile, damit Links kurz
    // bleiben und ein geteilter Link nicht den Standard mitschleppt.
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        for (const feld of Object.keys(standardRef.current)) {
            if (gleich(werte[feld], standardRef.current[feld])) {
                params.delete(feld);
            } else {
                params.set(feld, codieren(werte[feld]));
            }
        }
        const query = params.toString();
        const url = query ? `${window.location.pathname}?${query}` : window.location.pathname;
        window.history.replaceState(null, "", url);
    }, [werte]);

    const setWert = useCallback((feld, wert) => {
        setWerteState((vorher) => {
            const naechste = {...vorher, [feld]: wert};
            gemerkteWerteSchreiben(schluessel, naechste);
            return naechste;
        });
    }, [schluessel]);

    const setWerte = useCallback((teilwerte) => {
        setWerteState((vorher) => {
            const naechste = {...vorher, ...teilwerte};
            gemerkteWerteSchreiben(schluessel, naechste);
            return naechste;
        });
    }, [schluessel]);

    const zuruecksetzen = useCallback(() => {
        const frisch = {...standardRef.current};
        gemerkteWerteSchreiben(schluessel, frisch);
        setWerteState(frisch);
    }, [schluessel]);

    const weichtAb = useMemo(
        () => Object.keys(standardRef.current).some((feld) => !gleich(werte[feld], standardRef.current[feld])),
        [werte]
    );

    return {werte, setWert, setWerte, zuruecksetzen, weichtAb};
}

/**
 * Baustein "Alle Filter zurücksetzen" - erscheint nur, wenn tatsächlich
 * etwas vom Standard abweicht (Hausregel: leere/wirkungslose Bausteine
 * verschwinden vollständig statt deaktiviert dazustehen).
 */
export function AlleFilterZuruecksetzenLink({sichtbar, onClick}) {
    if (!sichtbar) {
        return null;
    }
    return (
        <button type="button" className="btn btn-link btn-sm p-0" onClick={onClick}>
            Alle Filter zurücksetzen
        </button>
    );
}

/**
 * Merkt die Scroll-Position (waagerecht und senkrecht) einer breiten Tabelle
 * je Seite in der Session (T2: "auch Scroll-Position der Tabelle, wenn
 * einfach machbar") - beim Rücksprung vom Artikel-Blatt landet die Tabelle
 * wieder an derselben Stelle. `bereit` erst true setzen, wenn die Tabelle
 * tatsächlich im DOM steht (Daten geladen), sonst geht die erste Restaurierung
 * ins Leere.
 */
export function useGemerkteTabellenScroll(seite, bereit) {
    const ref = useRef(null);
    const schluessel = `cosmos.scroll.${seite}`;
    const wiederhergestellt = useRef(false);

    useEffect(() => {
        if (!bereit || !ref.current || wiederhergestellt.current) {
            return;
        }
        wiederhergestellt.current = true;
        try {
            const roh = window.sessionStorage.getItem(schluessel);
            if (roh) {
                const [links, oben] = roh.split(",").map(Number);
                ref.current.scrollLeft = Number.isFinite(links) ? links : 0;
                ref.current.scrollTop = Number.isFinite(oben) ? oben : 0;
            }
        } catch {
            // Speicher nicht verfügbar - Tabelle startet einfach oben links.
        }
    }, [bereit, schluessel]);

    useEffect(() => {
        const el = ref.current;
        if (!bereit || !el) {
            return undefined;
        }
        let angefordert = null;
        const merken = () => {
            if (angefordert) {
                return;
            }
            angefordert = window.requestAnimationFrame(() => {
                angefordert = null;
                try {
                    window.sessionStorage.setItem(schluessel, `${el.scrollLeft},${el.scrollTop}`);
                } catch {
                    // Speicher nicht verfügbar - Position geht beim Verlassen einfach verloren.
                }
            });
        };
        el.addEventListener("scroll", merken, {passive: true});
        return () => {
            el.removeEventListener("scroll", merken);
            if (angefordert) {
                window.cancelAnimationFrame(angefordert);
            }
        };
    }, [bereit, schluessel]);

    return ref;
}

/**
 * Entfernt den Seiten-Parameter (Blättern allein zählt nicht als Filter)
 * sowie optional weitere Parameter, die zwar in der Adresszeile stehen,
 * aber keine Filter/Sortierung sind - etwa der aufklappbare Detail-/
 * Vorschau-Parameter einzelner Seiten (Bestellungen, Mails). Diese dürfen
 * nie gemerkt oder wiederhergestellt werden: ein späterer Aufruf mit einer
 * inzwischen ungültigen ID (gelöschter/verschobener Datensatz) würde die
 * Seite serverseitig zum Absturz bringen statt einfach nur ungefiltert zu
 * laden.
 */
function ohneSeitenParameter(suchstring, zusatzParameter = []) {
    const params = new URLSearchParams(suchstring);
    params.delete("page");
    zusatzParameter.forEach((name) => params.delete(name));
    return params.toString();
}

/**
 * Für Seiten, deren Filter/Suche/Sortierung serverseitig gehalten werden
 * (die Fremdkomponente `InertiaTable`: jede Änderung geht über
 * `router.reload({data: {...}})`, das Ergebnis kommt als Page-Prop zurück
 * und landet - weil es ein GET ist - auch sichtbar in der Adresszeile).
 * Diese Adresszeile ist zugleich das Signal: leer = frischer Aufruf ohne
 * eigene Filter, gefüllt = gerade aktiv gefiltert/sortiert.
 *
 * Beim Betreten ohne Parameter (Menü-Klick, "Zurück" von einer
 * Detailseite ohne mitgegebene Filter) wird einmalig der zuletzt gemerkte
 * Query-String erneut angefragt - als normaler Inertia-Hintergrund-Request
 * (`router.visit`, `replace: true`), also ohne Browser-Reload und ohne
 * zusätzlichen History-Eintrag. Trägt die URL bereits eigene Parameter
 * (geteilter Link, Browser-Zurück), hat das Vorrang vor dem Gedächtnis.
 *
 * @param {string} seite - eindeutiger Name der Seite (z. B. "weclapp.sales-orders").
 * @param {{benutzerId?: string|number, ignoriereParameter?: string[]}} [optionen]
 *        ignoriereParameter: Query-Parameter, die trotz Auftauchens in der
 *        Adresszeile nie gemerkt/wiederhergestellt werden (s. o., z. B.
 *        ["extradata", "preview"] bei Seiten mit Detail-/Vorschau-Parameter).
 * @returns {{weichtAb: boolean, zuruecksetzen: Function, tabellenKey: number}}
 *        tabellenKey: als React-`key` an die InertiaTable dieser Seite
 *        durchreichen (s. u., N4).
 */
export function useGemerkteServerFilter(seite, {benutzerId, ignoriereParameter = []} = {}) {
    const {url} = usePage();
    const schluessel = useMemo(() => speicherSchluessel("filter.server", seite, benutzerId), [seite, benutzerId]);
    const wiederhergestellt = useRef(false);
    // N4 (Rückmeldung 22.09.2026): Die Fremdkomponente InertiaTable liest ihr
    // Suchfeld nur einmal beim allerersten Rendern aus den Filtern
    // ("useState(filters.search)") und reagiert nicht auf spätere
    // Prop-Änderungen. Die Wiederherstellung läuft aber als Hintergrund-
    // Anfrage NACH diesem ersten Rendern - das Suchfeld bliebe sonst leer,
    // obwohl die Liste bereits gefiltert ist. Deshalb bekommt die
    // InertiaTable dieser Seite einen `key`, der sich genau einmal ändert,
    // sobald die Wiederherstellung (oder das Zurücksetzen) abgeschlossen
    // ist - React baut die Fremdkomponente dann einmal neu auf, und ihr
    // Suchfeld liest die Filter erneut, jetzt mit dem richtigen Wert.
    const [tabellenKey, setTabellenKey] = useState(0);

    useEffect(() => {
        if (wiederhergestellt.current) {
            return;
        }
        wiederhergestellt.current = true;
        const [pfad, suchstringRoh] = url.split("?");
        if (ohneSeitenParameter(suchstringRoh || "", ignoriereParameter)) {
            return;
        }
        let gemerkt = "";
        try {
            gemerkt = window.localStorage.getItem(schluessel) || "";
        } catch {
            gemerkt = "";
        }
        if (!gemerkt) {
            return;
        }
        router.visit(`${pfad}?${gemerkt}`, {
            replace: true,
            preserveScroll: true,
            preserveState: true,
            onFinish: () => setTabellenKey((n) => n + 1),
        });
        // Bewusst nur beim ersten Rendern der Seite - die Wiederherstellung
        // darf kein zweites Mal greifen, sobald der Nutzer selbst filtert.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [schluessel]);

    useEffect(() => {
        const [, suchstringRoh] = url.split("?");
        const bereinigt = ohneSeitenParameter(suchstringRoh || "", ignoriereParameter);
        if (bereinigt) {
            try {
                window.localStorage.setItem(schluessel, bereinigt);
            } catch {
                // s. o.
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [url, schluessel]);

    const [, aktuellerSuchstring] = url.split("?");
    const weichtAb = ohneSeitenParameter(aktuellerSuchstring || "", ignoriereParameter).length > 0;

    const zuruecksetzen = useCallback(() => {
        gemerktenEintragLoeschen(schluessel);
        const [pfad] = url.split("?");
        // Gleicher Grund wie oben (N4): das InertiaTable-Suchfeld muss nach
        // dem Zurücksetzen wieder leer erscheinen statt des zuvor
        // eingegebenen, jetzt ungültigen Suchbegriffs - daher auch hier der
        // key-Wechsel über tabellenKey nach Abschluss der Anfrage.
        router.visit(pfad, {
            replace: true,
            preserveScroll: true,
            preserveState: true,
            onFinish: () => setTabellenKey((n) => n + 1),
        });
    }, [schluessel, url]);

    return {weichtAb, zuruecksetzen, tabellenKey};
}

/**
 * Für die eigene `DataGrid`-Komponente (nicht das Fremdpaket InertiaTable):
 * liest den zuletzt gemerkten Zustand (Spaltenfilter, Sortierung, Suche,
 * Zusatzfilter) als `initial*`-Startwert und merkt jede spätere Änderung
 * über den vorhandenen `onStateChange`-Anschluss der Komponente - dasselbe
 * Prinzip wie die gespeicherten Ansichten der Produktinformationen, nur ohne
 * deren Namensverwaltung/Mehrfachansichten.
 *
 * @param {string} seite
 * @param {{benutzerId?: string|number, standardSorting?: Array}} [optionen]
 */
export function useGemerkteDataGridState(seite, {benutzerId, standardSorting = []} = {}) {
    const schluessel = useMemo(() => speicherSchluessel("filter.datagrid", seite, benutzerId), [seite, benutzerId]);
    const anfang = useMemo(() => gemerkteWerteLesen(schluessel), [schluessel]);
    const [zustand, setZustand] = useState(anfang);
    const ref = useRef(null);
    const wiederhergestellt = useRef(false);

    // additionalFilters/Suche/erweiterte Suche haben keine eigenen
    // initial*-Props an der Komponente - deshalb einmalig nach dem ersten
    // Rendern über dieselbe applyState()-Methode nachziehen, die auch beim
    // Wechsel einer gespeicherten Ansicht (Produktinformationen) genutzt wird.
    useEffect(() => {
        if (wiederhergestellt.current) {
            return;
        }
        wiederhergestellt.current = true;
        if (anfang && (anfang.additionalFilters || anfang.search || anfang.advancedSearch)) {
            ref.current?.applyState(anfang);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onStateChange = useCallback((neuerZustand) => {
        setZustand(neuerZustand);
        gemerkteWerteSchreiben(schluessel, neuerZustand);
    }, [schluessel]);

    const weichtAb = Boolean(
        zustand && (
            (zustand.filters && zustand.filters.length > 0)
            || (zustand.search && zustand.search.length > 0)
            || (zustand.additionalFilters && Object.values(zustand.additionalFilters).some((wert) => wert !== "" && wert !== null && wert !== undefined && wert !== false))
            || (zustand.sorting && JSON.stringify(zustand.sorting) !== JSON.stringify(standardSorting))
        )
    );

    const zuruecksetzen = useCallback(() => {
        gemerktenEintragLoeschen(schluessel);
        ref.current?.applyState({filters: [], sorting: standardSorting, additionalFilters: {}, search: ""});
    }, [schluessel, standardSorting]);

    return {
        ref,
        onStateChange,
        initialColumnFilters: anfang.filters || [],
        initialSorting: anfang.sorting && anfang.sorting.length ? anfang.sorting : standardSorting,
        weichtAb,
        zuruecksetzen,
    };
}
